/**
 * 📓 MINDSET JOURNAL — /mindset/journal
 *
 * Iter. 406.fr.35 — Chronological archive of every Mindset Q+A the user has
 * ever logged. Filters by holistic pillar (theme) + case-insensitive search.
 * Cursor-based "Voir plus" pagination so the page stays snappy even after
 * months of daily reflections.
 *
 * Hits GET /api/mindset/reflections.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Brain, Search, BookOpen, Sparkles, Loader2,
} from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const THEMES = [
  { key: 'sommeil',     fr: 'Sommeil',         en: 'Sleep',         color: 'text-indigo-300', bg: 'bg-indigo-500/15 ring-indigo-500/30' },
  { key: 'stress',      fr: 'Stress',          en: 'Stress',        color: 'text-rose-300',   bg: 'bg-rose-500/15 ring-rose-500/30' },
  { key: 'routines',    fr: 'Routines',        en: 'Routines',      color: 'text-amber-300',  bg: 'bg-amber-500/15 ring-amber-500/30' },
  { key: 'relations',   fr: 'Relations',       en: 'Relations',     color: 'text-pink-300',   bg: 'bg-pink-500/15 ring-pink-500/30' },
  { key: 'mindset_pro', fr: 'Mindset pro',     en: 'Pro mindset',   color: 'text-violet-300', bg: 'bg-violet-500/15 ring-violet-500/30' },
  { key: 'discipline',  fr: 'Discipline',      en: 'Discipline',    color: 'text-orange-300', bg: 'bg-orange-500/15 ring-orange-500/30' },
  { key: 'resilience',  fr: 'Résilience',      en: 'Resilience',    color: 'text-emerald-300',bg: 'bg-emerald-500/15 ring-emerald-500/30' },
  { key: 'gratitude',   fr: 'Gratitude',       en: 'Gratitude',     color: 'text-yellow-300', bg: 'bg-yellow-500/15 ring-yellow-500/30' },
  { key: 'nutrition',   fr: 'Nutrition',       en: 'Nutrition',     color: 'text-lime-300',   bg: 'bg-lime-500/15 ring-lime-500/30' },
];

const themeMeta = (k) => THEMES.find((t) => t.key === k) || null;

const fmtDate = (iso, fr) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(fr ? 'fr-CA' : 'en-CA', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

export default function MindsetJournal() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');
  const es = (language || 'fr').startsWith('es');
  // Iter. 411.mm2 — Trilingual fallback for logged questions:
  // ES → EN → FR when in ES; FR → EN when in FR; EN → FR when in EN.
  const pickLoggedQ = (r) => es
    ? (r.question_es || r.question_en || r.question_fr)
    : (fr ? (r.question_fr || r.question_en) : (r.question_en || r.question_fr));

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [total, setTotal] = useState(null);
  const [theme, setTheme] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchTimer = useRef(null);

  const fetchPage = useCallback(async (opts = {}) => {
    const { append = false, cursor = null } = opts;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (cursor) params.set('cursor', cursor);
      if (theme) params.set('theme', theme);
      if (debouncedSearch && debouncedSearch.length >= 2) {
        params.set('search', debouncedSearch);
      }
      const res = await api.get(`/mindset/reflections?${params.toString()}`);
      const data = res.data || {};
      setItems((prev) => append ? [...prev, ...(data.items || [])] : (data.items || []));
      setNextCursor(data.next_cursor || null);
      if (!append && data.total_estimate != null) setTotal(data.total_estimate);
    } catch { /* ignore — keep current items */ }
    finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [theme, debouncedSearch]);

  // Debounce search input (350ms)
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [search]);

  // Re-fetch when filters change
  useEffect(() => { fetchPage({ append: false, cursor: null }); }, [fetchPage]);

  const filtered = items;

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="mindset-journal-page">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate('/mindset')}
            className="w-9 h-9 rounded-full bg-secondary/60 flex items-center justify-center hover:bg-secondary transition-colors"
            data-testid="journal-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold flex items-center gap-1.5">
              <BookOpen className="w-3 h-3" />
              {fr ? 'Rituel Mindset' : 'Mindset Ritual'}
            </p>
            <h1 className="font-heading text-2xl uppercase tracking-tight text-foreground">
              {fr ? 'Mes réflexions' : 'My reflections'}
            </h1>
          </div>
          {total != null && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/10 ring-1 ring-cyan-500/30" data-testid="journal-total-chip">
              <Brain className="w-3.5 h-3.5 text-cyan-300" />
              <span className="text-xs font-black text-cyan-200 tabular-nums">{total}</span>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="mb-3 relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={fr ? 'Rechercher dans tes réflexions…' : 'Search your reflections…'}
            className="pl-9 bg-zinc-900/60 border-border focus-visible:ring-amber-400/40"
            data-testid="journal-search-input"
            maxLength={80}
          />
        </div>

        {/* Theme filter chips */}
        <div className="mb-5 -mx-1 flex flex-wrap gap-1.5" data-testid="journal-theme-chips">
          <button
            onClick={() => setTheme(null)}
            className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ring-1 transition-all ${
              theme === null
                ? 'bg-amber-500/20 ring-amber-500/50 text-amber-200'
                : 'bg-secondary/40 ring-border text-muted-foreground hover:text-foreground'
            }`}
            data-testid="journal-theme-all"
          >
            {fr ? 'Tous' : 'All'}
          </button>
          {THEMES.map((t) => (
            <button
              key={t.key}
              onClick={() => setTheme(theme === t.key ? null : t.key)}
              className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ring-1 transition-all ${
                theme === t.key
                  ? `${t.bg} ring-2 ${t.color}`
                  : 'bg-secondary/40 ring-border text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`journal-theme-${t.key}`}
            >
              {fr ? t.fr : t.en}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-8 text-center">
              <Brain className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {debouncedSearch || theme
                  ? (fr ? 'Aucune réflexion ne correspond à ce filtre.' : 'No reflection matches this filter.')
                  : (fr ? "Tu n'as pas encore loggé de réflexion. Commence aujourd'hui." : 'You have not logged any reflection yet. Start today.')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((r, idx) => {
              const meta = themeMeta(r.theme);
              return (
                <motion.div
                  key={`${r.date}-${idx}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.03, 0.4) }}
                >
                  <Card className="bg-card/70 border-border/60" data-testid={`journal-entry-${r.date}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold tabular-nums">
                          {fmtDate(r.date, fr)}
                        </span>
                        {meta && (
                          <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ring-1 ${meta.bg} ${meta.color}`}>
                            {fr ? meta.fr : meta.en}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-heading text-amber-200/90 leading-snug mb-2">
                        {pickLoggedQ(r)}
                      </p>
                      <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed" data-testid="journal-answer">
                        {r.answer}
                      </p>
                      {r.coach_feedback?.message && (
                        <div className="mt-3 pt-3 border-t border-border/40 flex items-start gap-2">
                          <Sparkles className="w-3.5 h-3.5 text-orange-300 shrink-0 mt-0.5" />
                          <p className="text-xs text-orange-200/85 italic leading-relaxed">
                            {r.coach_feedback.message}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}

            {nextCursor && (
              <Button
                onClick={() => fetchPage({ append: true, cursor: nextCursor })}
                disabled={loadingMore}
                variant="outline"
                className="w-full mt-2 border-border/60 text-muted-foreground hover:text-foreground"
                data-testid="journal-load-more-btn"
              >
                {loadingMore ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {fr ? 'Voir plus' : 'Load more'}
              </Button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
